import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { formatCategoryName, getCategoryPath, slugifyCategory } from '../seoConfig';
import Logo from './Logo';

const MoonIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
  </svg>
);

const SunIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="5" />
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

const GridIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const CloseIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12" /><path d="M18 6L6 18" />
  </svg>
);

const ChevronIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

// Every tool on the site, grouped by the job it does. Names only: the drawer
// is in every page's HTML, and a description per tool would be sixty words of
// boilerplate repeated across the site. Each group's tile shows a sample of
// what the group makes rather than a generic icon - the site's own output is
// the most recognisable thing it has.
export const TOOL_GROUPS = [
  {
    id: 'styling', label: 'Fonts & styling', hint: 'Restyle text to copy', glyph: '𝓐',
    links: [
      { to: '/', label: 'Font Generator', end: true },
      { to: '/text-styler/', label: 'Text Styler' },
      { to: '/font-image-generator/', label: 'Font Image Generator' },
      { to: '/number-font-generator/', label: 'Number Font Generator' },
      { to: '/tattoo-font-generator/', label: 'Tattoo Font Generator' },
    ],
  },
  {
    id: 'names', label: 'Names & profiles', hint: 'Handles and initials', glyph: '@',
    links: [
      { to: '/username-generator/', label: 'Username Generator' },
      { to: '/monogram-maker/', label: 'Monogram Maker' },
    ],
  },
  {
    id: 'utilities', label: 'Text utilities', hint: 'Count, case, repeat, flip', glyph: 'Aa',
    links: [
      { to: '/word-counter/', label: 'Word Counter' },
      { to: '/case-converter/', label: 'Case Converter' },
      { to: '/text-repeater/', label: 'Text Repeater' },
      { to: '/backwards-text/', label: 'Backwards Text' },
      { to: '/fullscreen-text/', label: 'Full Screen Text' },
    ],
  },
  {
    id: 'symbols', label: 'Symbols & faces', hint: 'Characters to drop in', glyph: '✦',
    links: [
      { to: '/unicode-symbols/', label: 'Symbols' },
      { to: '/kaomoji/', label: 'Kaomoji' },
      { to: '/lenny-faces/', label: 'Lenny Faces' },
      { to: '/wingdings-translator/', label: 'Wingdings Translator' },
    ],
  },
  {
    id: 'art', label: 'Text art', hint: 'Pictures made of characters', glyph: '⣿',
    links: [
      { to: '/dot-art-generator/', label: 'Dot Art Generator' },
      { to: '/ascii-art-generator/', label: 'ASCII Art Generator' },
    ],
  },
  {
    id: 'guides', label: 'Help & guides', hint: 'Where styled text works', glyph: '?',
    links: [
      { to: '/unicode-font-compatibility/', label: 'Compatibility Guide' },
      { to: '/blog/', label: 'Blog' },
    ],
  },
];

export const TOOL_LINKS = TOOL_GROUPS.flatMap((g) => g.links);

// The 32 font-generator categories grouped by look. Keyed by slug so a renamed
// category still lands in its group; one the map does not know goes to "More
// styles" rather than disappearing from the menu.
const FONT_GROUPS = [
  { id: 'social', label: 'For social apps', hint: 'Bios, names, status', glyph: '#', slugs: ['instagram-fonts', 'facebook-fonts', 'twitter-fonts', 'discord-fonts', 'twitch-fonts', 'whatsapp-fonts'] },
  { id: 'decorative', label: 'Decorative', hint: 'Bubbles, hearts, sparkle', glyph: 'Ⓐ', slugs: ['fancy-fonts', 'cool-fonts', 'decorated-fonts', 'heart-decorated-fonts', 'bubble-fonts', 'symbolic-fonts', 'cute-fonts', 'aesthetic-fonts', 'ethereal-fonts', 'swirly-fonts'] },
  { id: 'classic', label: 'Script & classic', hint: 'Cursive, serif, bold', glyph: '𝒮', slugs: ['cursive-fonts', 'script-fonts', 'calligraphy-fonts', 'italics-fonts', 'serif-fonts', 'bold-fonts', 'gothic-fonts', 'monospace-fonts', 'small-fonts'] },
  { id: 'dark', label: 'Dark & glitch', hint: 'Creepy and corrupted', glyph: 'Z̷', slugs: ['creepy-fonts', 'glitch-fonts', 'cursed-fonts', 'zalgo-fonts'] },
  { id: 'lines', label: 'Lines through text', hint: 'Underline, strike, cross out', glyph: 'U̲', slugs: ['underline-text', 'strikethrough-text', 'cross-out-fonts'] },
];

// One collapsible group. A native <details>, so the links inside a closed
// group are still in the page's HTML - for crawlers, and for anyone with
// scripting off, who can still open it - and the summary is a real button to
// keyboards and screen readers without any ARIA of our own.
const MenuGroup = ({ group, open, onToggle, children }) => {
  const count = group.links.length;
  const filtered = group.shown.length !== count;
  return (
    <details
      open={open}
      onToggle={(e) => onToggle(group.id, e.currentTarget.open)}
      className="group rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] open:border-purple-200 open:shadow-[0_6px_18px_rgba(109,40,217,0.08)] dark:open:border-purple-800"
    >
      <summary className="flex min-h-14 cursor-pointer list-none items-center gap-3 rounded-2xl px-2.5 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-500 [&::-webkit-details-marker]:hidden">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-fuchsia-100 text-lg font-black leading-none text-purple-700 ring-1 ring-purple-200 dark:from-purple-900/60 dark:to-fuchsia-900/40 dark:text-purple-100 dark:ring-purple-800"
        >
          {group.glyph}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-black text-[var(--color-text)]">{group.label}</span>
          <span className="block truncate text-xs text-[var(--color-muted)]">{group.hint}</span>
        </span>
        <span className="shrink-0 rounded-full bg-[var(--color-card)] px-2 py-0.5 text-[0.7rem] font-bold text-[var(--color-muted)] ring-1 ring-[var(--color-border)]">
          {filtered ? `${group.shown.length} of ${count}` : count}
        </span>
        <ChevronIcon className="h-4 w-4 shrink-0 text-[var(--color-primary)] transition-transform duration-200 group-open:rotate-90" />
      </summary>
      <ul className="grid gap-1 px-2 pb-2">{children}</ul>
    </details>
  );
};

const Header = ({ toggleDarkMode, isDarkMode, categories = [] }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';

  const categoryLinks = useMemo(
    () => categories.map((c) => ({ to: getCategoryPath(c), label: formatCategoryName(c), slug: slugifyCategory(c) })),
    [categories],
  );

  const fontGroups = useMemo(() => {
    const placed = new Set();
    const groups = FONT_GROUPS.map((g) => {
      const links = g.slugs.map((s) => categoryLinks.find((l) => l.slug === s)).filter(Boolean);
      links.forEach((l) => placed.add(l.slug));
      return { ...g, links };
    }).filter((g) => g.links.length);
    const rest = categoryLinks.filter((l) => !placed.has(l.slug));
    if (rest.length) groups.push({ id: 'more', label: 'More styles', hint: 'Everything else', glyph: '+', links: rest });
    return groups;
  }, [categoryLinks]);

  const q = query.trim().toLowerCase();
  const match = (l) => !q || l.label.toLowerCase().includes(q);
  const isHere = (to) => normalizedPath === (to.replace(/\/+$/, '') || '/');
  const filterGroups = (groups) => groups
    .map((g) => ({ ...g, shown: g.links.filter(match) }))
    .filter((g) => g.shown.length);
  const toolGroups = filterGroups(TOOL_GROUPS);
  const shownFontGroups = filterGroups(fontGroups);

  // Which groups are open. The one holding the current page starts open, so
  // the drawer opens where the reader already is; a search opens every group
  // with a match, because a hit inside a closed group is a hit nobody sees.
  const [openGroups, setOpenGroups] = useState(() => new Set());
  useEffect(() => {
    const here = [...TOOL_GROUPS, ...fontGroups].find((g) => g.links.some((l) => isHere(l.to)));
    setOpenGroups(here ? new Set([here.id]) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, fontGroups]);
  const isOpen = (id) => Boolean(q) || openGroups.has(id);
  const toggleGroup = (id, nowOpen) => {
    if (q) return;
    setOpenGroups((prev) => {
      if (prev.has(id) === nowOpen) return prev;
      const next = new Set(prev);
      if (nowOpen) next.add(id); else next.delete(id);
      return next;
    });
  };

  // Route change closes the drawer; Escape closes it; body scroll is locked
  // while it is open; focus goes to the close button and comes back to the
  // trigger afterwards.
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    const trigger = triggerRef.current;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      trigger?.focus();
    };
  }, [open]);

  const navLinkClass = ({ isActive }) =>
    `inline-flex min-h-11 items-center rounded-lg px-3 py-2 text-sm font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
      isActive
        ? 'bg-[var(--color-card)] text-purple-700 shadow-sm ring-1 ring-purple-100 dark:bg-purple-900/60 dark:text-purple-100 dark:ring-purple-800'
        : 'text-slate-700 hover:bg-[var(--color-card)] hover:text-purple-700 dark:text-slate-200 dark:hover:bg-gray-800 dark:hover:text-purple-200'
    }`;

  // Blog sits in the drawer's guides group but is not a tool, so it must not
  // light the All Tools button up.
  const isTool = TOOL_LINKS.some((l) => l.to !== '/' && l.to !== '/blog/' && normalizedPath === l.to.replace(/\/+$/, ''));
  const isCategory = pathname.startsWith('/fonts');
  const triggerClass = `inline-flex min-h-11 items-center gap-2 rounded-xl border px-3 text-sm font-black shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
    isTool || isCategory
      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
      : 'border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-primary)] hover:border-[var(--color-primary)]'
  }`;

  const itemClass = (active) =>
    `flex min-h-11 items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-500 ${
      active
        ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-200 dark:bg-purple-900/50 dark:text-purple-100 dark:ring-purple-800'
        : 'text-[var(--color-text)] hover:bg-purple-50 hover:text-[var(--color-primary)] dark:hover:bg-gray-800'
    }`;

  return (
    <header className="relative z-50 bg-[var(--color-bg)] pb-1 pt-2 md:pt-4">
      <div className="site-container relative">
        <div className="grid min-h-[48px] grid-cols-[1fr_auto] items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 shadow-[0_4px_16px_rgba(109,40,217,0.06)] lg:px-5 lg:py-3">
          <div className="flex min-w-0 items-center">
            <Logo imageClassName="h-7 w-auto sm:h-9 md:h-11" />
          </div>

          <div className="flex items-center justify-self-end gap-2">
            <nav className="hidden items-center gap-1 lg:flex" aria-label="Main navigation">
              <NavLink to="/" className={navLinkClass} end>Home</NavLink>
              <NavLink to="/fonts/" className={navLinkClass}>Fonts</NavLink>
              <NavLink to="/blog/" className={navLinkClass}>Blog</NavLink>
            </nav>

            <button
              ref={triggerRef}
              type="button"
              onClick={() => setOpen(true)}
              aria-expanded={open}
              aria-controls="site-sidebar"
              className={triggerClass}
            >
              <GridIcon className="h-4 w-4" />
              <span className="hidden sm:inline">All Tools</span>
              <span className="sr-only sm:hidden">All tools and font generators</span>
            </button>

            <button
              type="button"
              onClick={toggleDarkMode}
              className="inline-flex min-h-11 w-11 items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-sm font-black text-[var(--color-primary)] shadow-sm transition-all hover:border-[var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 lg:w-auto lg:px-4"
              aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
              <span className="hidden lg:inline">{isDarkMode ? 'Light' : 'Dark'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Overlay */}
      <div
        onClick={() => setOpen(false)}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />

      {/* The drawer is always in the document — its links are part of every
          page's HTML for crawlers and for anyone with scripting off — and is
          moved off-canvas rather than removed. `visibility` keeps it out of
          the tab order while closed. */}
      <aside
        id="site-sidebar"
        ref={panelRef}
        aria-label="All tools and font generators"
        aria-hidden={!open}
        className={`fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l border-[var(--color-border)] bg-[var(--color-card)] shadow-[-12px_0_34px_rgba(109,40,217,0.16)] transition-transform duration-200 ${
          open ? 'translate-x-0' : 'invisible translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3">
          <p className="text-sm font-black uppercase tracking-wide text-[var(--color-primary)]">Tools &amp; Fonts</p>
          <button
            ref={closeRef}
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-primary)] transition-colors hover:border-[var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-[var(--color-border)] px-4 py-3">
          <label htmlFor="sidebar-search" className="sr-only">Find a tool or font style</label>
          <input
            id="sidebar-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a tool or font style…"
            className="h-11 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-4 text-sm text-[var(--color-text)] placeholder:text-[var(--color-muted)] focus:border-[var(--color-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
        </div>

        <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-3" aria-label="Tools and font generators">
          <p className="mt-1 px-3 text-xs font-black uppercase tracking-wide text-[var(--color-muted)]">Tools</p>
          <div className="mt-1.5 grid gap-1.5">
            {toolGroups.map((g) => (
              <MenuGroup key={g.id} group={g} open={isOpen(g.id)} onToggle={toggleGroup}>
                {g.shown.map((l) => (
                  <li key={l.to}>
                    <NavLink to={l.to} end={l.end} className={({ isActive }) => itemClass(isActive)}>{l.label}</NavLink>
                  </li>
                ))}
              </MenuGroup>
            ))}
            {toolGroups.length === 0 ? <p className="px-3 py-2 text-sm text-[var(--color-muted)]">No tool matches.</p> : null}
          </div>

          <div className="mt-5 flex items-baseline justify-between gap-3 px-3">
            <p className="text-xs font-black uppercase tracking-wide text-[var(--color-muted)]">Font Generators</p>
            {/* These are the 32 generator categories, not 32 styles — the
                styles number 474. Two different counts were both labelled
                "styles", which is how the homepage came to advertise one
                while showing the other. */}
            <NavLink to="/fonts/" end className="text-xs font-bold text-[var(--color-primary)] underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500">
              All {categoryLinks.length} categories
            </NavLink>
          </div>
          <div className="mt-1.5 grid gap-1.5">
            {shownFontGroups.map((g) => (
              <MenuGroup key={g.id} group={g} open={isOpen(g.id)} onToggle={toggleGroup}>
                {g.shown.map((l) => (
                  <li key={l.to}>
                    <a href={l.to} className={itemClass(isHere(l.to))}>{l.label}</a>
                  </li>
                ))}
              </MenuGroup>
            ))}
            {shownFontGroups.length === 0 ? <p className="px-3 py-2 text-sm text-[var(--color-muted)]">No font style matches.</p> : null}
          </div>
        </nav>
      </aside>
    </header>
  );
};

export default Header;
